import { ActionConfirmation, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useId, useState, type FormEvent } from "react";
import {
  endTeachingAssignment,
  getStudentTeachingAssignments,
  type TeachingAssignment,
} from "../api.js";
import { ApiError } from "../api.js";
import { formatDate } from "../format.js";
import { ConfirmDialog } from "../ui/Dialog.js";
import { useAsync } from "../useAsync.js";
import { groupTeachingAssignments, type TeachingAssignmentGroup } from "./teachingAssignmentGroups.js";

/** Owner-managed private teaching relationships for one student. */
export function TeachingAssignments({ studentId }: { studentId: string }) {
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const loaded = useAsync(() => getStudentTeachingAssignments(studentId), [studentId]);
  const assignments = loaded.data?.assignments ?? [];
  const groups = groupTeachingAssignments(assignments);

  if (loaded.loading && !loaded.data) return <p className="empty">Loading teaching assignments…</p>;
  if (loaded.error && !loaded.data) return <div className="error">{loaded.error}</div>;
  if (!loaded.data) return null;

  return (
    <>
      <h2>Teaching assignments</h2>
      {outcome && <ActionConfirmation {...outcome} reveal />}
      <div className="panel">
        {assignments.length === 0 && (
          <p className="empty">No active teaching assignments recorded.</p>
        )}
        {groups.map((group) => (
          <TeachingAssignmentCard
            key={group.key}
            group={group}
            studentId={studentId}
            onEnded={(result) => { if (result) setOutcome(result); loaded.reload(); }}
          />
        ))}
      </div>
    </>
  );
}

function TeachingAssignmentCard({ group, studentId, onEnded }: {
  group: TeachingAssignmentGroup;
  studentId: string;
  onEnded: (outcome?: ActionOutcome) => void;
}) {
  const headingId = useId();
  return (
    <section className="settings-plan teaching-assignment-group" aria-labelledby={headingId}>
      <div className="spread teaching-assignment-heading">
        <div>
          <h3 id={headingId}>{group.tutorName ?? "Tutor unavailable"}</h3>
          <div className="small muted">{group.instrument ?? "Other teaching scope"}</div>
        </div>
        <span className="tag">Active</span>
      </div>
      <ul className="teaching-assignment-lengths" aria-label="Lesson lengths">
        {group.lessonLengths.map(length => <li key={length} className="tag">{length}</li>)}
      </ul>
      <details className="fold teaching-assignment-details">
        <summary>Manage assignments</summary>
        {group.assignments.map(assignment => (
          <TeachingAssignmentRow key={assignment.id} assignment={assignment} studentId={studentId} onEnded={onEnded} />
        ))}
      </details>
    </section>
  );
}

function TeachingAssignmentRow({
  assignment,
  studentId,
  onEnded,
}: {
  assignment: TeachingAssignment;
  studentId: string;
  onEnded: (outcome?: ActionOutcome) => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProblem(null);
    setConfirmOpen(true);
  }

  async function endAssignment() {
    const trimmedReason = reason.trim();
    if (busy) return;

    setBusy(true);
    setProblem(null);
    try {
      await endTeachingAssignment(studentId, assignment.id, {
        reason: trimmedReason,
        version: assignment.version,
      });
      setConfirmOpen(false);
      onEnded({ variant: "success", title: "Teaching assignment ended", description: assignment.tutorName, details: [{ value: assignment.serviceName ?? assignment.instrument ?? "Teaching scope recorded" }] });
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "teaching_assignment_changed") {
        setConfirmOpen(false);
        setProblem("This assignment changed while you were viewing it. The list has been reloaded; try again if it is still active.");
        onEnded();
      } else {
        setProblem(cause instanceof Error ? cause.message : "That assignment could not be ended.");
      }
    } finally {
      setBusy(false);
    }
  }

  // Ending a teaching scope stays an explicit review step; the note is optional.
  return (
    <form className="settings-plan teaching-assignment-scope" noValidate onSubmit={submit}>
      <div>
        <strong>{assignment.serviceName ?? assignment.instrument ?? "Teaching scope recorded"}</strong>
        <div className="small muted">
          Starts {formatDate(assignment.startsAt)}
          {assignment.endsAt ? ` · ends ${formatDate(assignment.endsAt)}` : " · ongoing"}
        </div>
      </div>

      <label style={{ display: "block", marginTop: 10 }}>
        <span className="small muted">Note (optional)</span>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={2000}
          rows={2}
          style={{ display: "block", width: "100%", marginTop: 3 }}
        />
      </label>

      {problem && (
        <div className="error small" role="alert" style={{ marginTop: 10 }}>
          {problem}
        </div>
      )}
      <button type="submit" className="quiet small" disabled={busy} style={{ marginTop: 10 }}>
        {busy ? "Ending…" : "End assignment"}
      </button>

      {confirmOpen && (
        <ConfirmDialog
          title={`End ${assignment.tutorName ?? "this tutor"}'s teaching assignment?`}
          description={assignment.serviceName ?? assignment.instrument ?? "Teaching assignment"}
          confirmLabel="End assignment"
          confirmingLabel="Ending…"
          confirmClassName="action-danger"
          busy={busy}
          onConfirm={endAssignment}
          onClose={() => setConfirmOpen(false)}
        >
          <p className="small" style={{ marginTop: 0 }}>
            This ends the active teaching relationship from the Hub. It does not delete historic lessons or notes.
          </p>
          {reason.trim() && <p className="small" style={{ marginBottom: problem ? 10 : 0 }}>
            <strong>Reason:</strong> {reason.trim()}
          </p>}
          {problem && <div className="error small" role="alert">{problem}</div>}
        </ConfirmDialog>
      )}
    </form>
  );
}
